import {
  Schedule,
  ZoneProjection,
  useLazyZoneProjectionQuery,
  useZoneHistoryQuery,
} from "@/shared/api";
import { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";

export type ZoneProjectionRendererProps = {
  isOverflownProjectionsVisible: boolean;
  zoneProjections: ZoneProjection[];
  isOutsideView: (projection: [number, number][]) => boolean;
  isObserverCamera?: boolean;
};

export type ZoneProjectionRendererFn = ({
  zoneProjections,
  isOutsideView,
}: ZoneProjectionRendererProps) => React.ReactNode;

type ZoneProjectionsProps = {
  selectedCamera: string | null;
  imageDimensions: {
    width: number;
    height: number;
  };
  imageSize: {
    width: number;
    height: number;
  };
  isOverflownProjectionsVisible?: boolean;
  children?: ZoneProjectionRendererFn;
  getOnDateTime: () => string | undefined;
  activeSchedule: Schedule | null;
};

function ZoneProjectionsInner({
  selectedCamera,
  imageSize,
  activeSchedule,
  isOverflownProjectionsVisible = false,
  getOnDateTime,
  children,
}: ZoneProjectionsProps) {
  const { data: zoneHistory } = useZoneHistoryQuery(
    {
      historyId: activeSchedule?.id!,
    },
    {
      skip: !activeSchedule,
    }
  );

  const [trigger] = useLazyZoneProjectionQuery();
  const [zoneProjections, setZoneProjections] = useState<
    (ZoneProjection & {
      isGondolaProjection?: boolean;
    })[]
  >([]);

  const [isLoadingZones, setIsLoadingZones] = useState(false);

  const currentCameraRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const isOutsideView = (polygon: [number, number][]) => {
    return polygon.every(
      ([x, y]) => x < 0 || x > imageSize.width || y < 0 || y > imageSize.height
    );
  };

  useEffect(() => {
    currentCameraRef.current = selectedCamera;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!(activeSchedule && zoneHistory && selectedCamera)) {
      setZoneProjections([]);
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoadingZones(true);

    const onDateTime = getOnDateTime?.();

    Promise.all(
      zoneHistory.results.map((zone) =>
        trigger(
          {
            historyId: activeSchedule.id,
            zoneId: zone.id,
            cameraId: selectedCamera,
            on_datetime: onDateTime,
          },
          true
        )
          .unwrap()
          .catch((error) => {
            if (error.name !== "AbortError") {
              toast.error("Failed to load zone projections", {
                id: "zone-projection-error",
                duration: 5000,
              });
              return null;
            }
            return null;
          })
      )
    )
      .then((results) => {
        if (
          currentCameraRef.current === selectedCamera &&
          !abortController.signal.aborted
        ) {
          const projections = results
            .filter(Boolean)
            .map((result) => result?.result)
            .filter(
              (
                projection
              ): projection is ZoneProjection & {
                isGondolaProjection?: boolean;
              } => projection !== undefined
            );
          setZoneProjections(projections);
        }
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          toast.error(`Failed to load zone projections: ${error.message}`, {
            id: "zone-projection-error",
            duration: 5000,
          });
        }
      })
      .finally(() => {
        if (currentCameraRef.current === selectedCamera) {
          setIsLoadingZones(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [activeSchedule, zoneHistory, selectedCamera, getOnDateTime]);

  if (isLoadingZones) {
    return (
      children?.({
        zoneProjections: [],
        isOutsideView,
        isOverflownProjectionsVisible,
        isObserverCamera: false,
      }) || null
    );
  }

  if (zoneProjections.length === 0) return null;

  return (
    <>
      {children?.({
        zoneProjections,
        isOutsideView,
        isOverflownProjectionsVisible,
      })}
    </>
  );
}

export function ZoneProjections(props: ZoneProjectionsProps) {
  return <ZoneProjectionsInner {...props} />;
}
